import { useState } from 'react';
import { useStyles, Button } from '@archipro-design/aria';
import { DownloadLine } from '@archipro-design/icons';
import * as S from './DetailPageButtons.style';
import { IslandTooltip } from './IslandTooltip';
import { downloadImagesZip } from './download-images';
import { useUserLoggedIn } from '~/modules/user';

export interface DownloadImagesConfig {
    /** What the page is, for the label: "Download all images for this Product". */
    itemLabel: 'Product' | 'Project' | 'Article';
    /** The page's title, for the zip's file name. */
    title: string;
    /**
     * The page's own photographs as originals, read when the button is pressed
     * — the article's are only known once its modules are on the page.
     */
    getImages: () => string[];
}

type State =
    | { kind: 'idle' }
    | { kind: 'working'; done: number; total: number }
    | { kind: 'finished'; message: string };

/**
 * Staff island control: every photograph this page is about — the product's
 * slider, the project's grid and banner, the article's banner and body — as
 * the original uploads, in one zip. Nothing else on the page: no logos, no
 * tiles for other items.
 */
export const DownloadImagesButton = ({
    itemLabel,
    title,
    getImages,
}: DownloadImagesConfig) => {
    const { css } = useStyles();
    const { user, isLoggedIn } = useUserLoggedIn();
    const [state, setState] = useState<State>({ kind: 'idle' });

    // ArchiPro staff only. The island also shows for a client on their own
    // items, and taking a page's originals is a team tool, not theirs.
    if (!isLoggedIn || !user.IsArchiproStaff) return null;

    const idleLabel = `Download all images for this ${itemLabel}`;
    const label =
        state.kind === 'working'
            ? `Downloading ${state.done} of ${state.total}…`
            : state.kind === 'finished'
              ? state.message
              : idleLabel;

    const run = async () => {
        if (state.kind === 'working') return;
        const images = getImages();
        if (images.length === 0) {
            setState({ kind: 'finished', message: 'No images on this page' });
            setTimeout(() => setState({ kind: 'idle' }), 3000);
            return;
        }
        // Every exit says something and returns the button to idle. The zip
        // holds each original in memory at once, so a large page can run the
        // tab out of room and throw — and an unhandled throw left the control
        // reading "Downloading…" for the life of the page, with no way back
        // short of a reload.
        let message: string;
        try {
            const { saved, failed } = await downloadImagesZip(
                images,
                title,
                ({ done, total }) => setState({ kind: 'working', done, total }),
            );
            message =
                saved === 0
                    ? 'The images could not be downloaded'
                    : failed > 0
                      ? `Downloaded ${saved}; ${failed} could not be read`
                      : `Downloaded ${saved} image${saved === 1 ? '' : 's'}`;
        } catch {
            message = 'The images could not be downloaded';
        }
        setState({ kind: 'finished', message });
        setTimeout(() => setState({ kind: 'idle' }), 3000);
    };

    return (
        // The progress and the outcome hold the label up on their own: both
        // have to be readable after the pointer has moved on.
        <IslandTooltip label={label} forceVisible={state.kind !== 'idle'}>
            <Button
                aria-label={idleLabel}
                aria-busy={state.kind === 'working'}
                className={css(S.ActionButton)}
                icon={<DownloadLine />}
                size={20}
                color="white"
                disabled={state.kind === 'working'}
                variables={{
                    iconPadding: 0,
                    // The copy-link circle's padding, so the two sit the same size.
                    buttonPadding: '0.8125rem',
                    buttonRadius: `50%`,
                }}
                onClick={(event) => {
                    event.preventDefault();
                    void run();
                }}
            />
        </IslandTooltip>
    );
};
